import { useQueryClient } from '@tanstack/react-query';
import {
  FETCH_PAYMENT_FAILURE,
  FETCH_PAYMENT_REQUEST,
} from 'constants/payment';
import type { Payment } from 'models/payment';
import { useCallback } from 'react';
import { useStore } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getOrder } from 'selectors/entities/order/order';
import { getCurrentLocale, untilIsFetched } from 'selectors/ui/ui';
import { useDispatch } from 'store/configureStore';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import { paymentQueries } from './paymentQueries';

type FetchPaymentByOrderParams = {
  orderId?: string;
  token?: string;
};

type FetchPaymentByOrderResult = {
  success: boolean;
  payment?: Payment;
};

export const usePayment = () => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const queryClient = useQueryClient();

  const fetchPaymentByOrder = useCallback(
    async (
      params: FetchPaymentByOrderParams = {},
    ): Promise<FetchPaymentByOrderResult> => {
      const state = store.getState();
      const currentLocale = getCurrentLocale(state);
      const currentOrder = getOrder(state);
      const { orderId, token } =
        params.orderId && params.token
          ? params
          : { orderId: currentOrder?.id, token: currentOrder?.token };

      await untilIsFetched(store.getState, 'payment');

      if (!orderId || !token) {
        dispatch({ type: FETCH_PAYMENT_REQUEST });
        errorNotifier.notify(
          `${FETCH_PAYMENT_FAILURE} Invalid order id (${orderId}) or token (${token}) when fetching order payment`,
        );
        dispatch({ type: FETCH_PAYMENT_FAILURE });
        return { success: false };
      }

      try {
        const payment = await queryClient.fetchQuery(
          paymentQueries.byOrder(
            { orderId, token, locale: currentLocale },
            { dispatch },
          ),
        );

        return { success: true, payment };
      } catch (_error) {
        return { success: false };
      }
    },
    [dispatch, queryClient, store],
  );

  const refetchPayment = useCallback(async () => {
    await fetchPaymentByOrder();
  }, [fetchPaymentByOrder]);

  return { fetchPaymentByOrder, refetchPayment };
};
